/**
 * The Configuration form's state rules, as pure functions (f2.1).
 *
 * Kept out of the components so the rules -- what counts as a change, what is
 * sent, what the client refuses before a round trip -- are testable on their
 * own and shared by every section rather than reimplemented per field.
 *
 * Nothing here knows a setting key, a label or an option list. Every control
 * is derived from the metadata the SERVER returned, which is what lets a later
 * phase add a section with no frontend change (BR-F2.1-11).
 */

import type { ErrorItem, Setting, SettingChange, SettingsCatalog } from '@/api/client';

/** The value types this build has a control for. Anything else is read-only. */
export const EDITABLE_TYPES: ReadonlySet<string> = new Set([
  'STRING',
  'INTEGER',
  'BOOLEAN',
  'ENUM',
]);

/**
 * What the Admin has typed but not saved, keyed by setting key.
 *
 * An INTEGER is held as the text in its input, so a half-typed or invalid
 * number stays exactly as typed instead of being coerced under the cursor.
 */
export type Draft = Readonly<Record<string, string | boolean>>;

export function isEditable(setting: Setting): boolean {
  return EDITABLE_TYPES.has(setting.value_type);
}

/** The drafted value for a setting, or undefined when it has not been touched. */
export function drafted(setting: Setting, draft: Draft): string | boolean | undefined {
  return draft[setting.key];
}

/** The value a control shows: the draft when there is one, else the stored value. */
export function displayedValue(setting: Setting, draft: Draft): string | boolean {
  const value = drafted(setting, draft);
  if (value !== undefined) return value;
  if (setting.value_type === 'BOOLEAN') return setting.value === true;
  return String(setting.value);
}

/** What would be SENT for a drafted value -- a number for a well-formed integer. */
export function submittedValue(setting: Setting, raw: string | boolean): unknown {
  if (setting.value_type === 'INTEGER' && typeof raw === 'string') {
    const trimmed = raw.trim();
    // Anything that is not a plain whole number is sent as typed, and the
    // server -- the actual boundary -- refuses it with its own message.
    return /^-?\d+$/.test(trimmed) ? Number(trimmed) : raw;
  }
  return raw;
}

/** Whether a drafted value differs from what is stored. */
export function isChanged(setting: Setting, draft: Draft): boolean {
  const value = drafted(setting, draft);
  if (value === undefined) return false;
  return submittedValue(setting, value) !== setting.value;
}

export function allSettings(catalog: SettingsCatalog): Setting[] {
  return catalog.sections.flatMap((section) => section.settings);
}

/**
 * Only the keys whose value differs from the loaded catalogue.
 *
 * An edit reverted by hand to the stored value is not a change, so it is not
 * sent and does not count as pending.
 */
export function pendingChanges(catalog: SettingsCatalog, draft: Draft): SettingChange[] {
  const changes: SettingChange[] = [];
  for (const setting of allSettings(catalog)) {
    const value = drafted(setting, draft);
    if (value === undefined || !isEditable(setting) || !isChanged(setting, draft)) continue;
    changes.push({ key: setting.key, value: submittedValue(setting, value) });
  }
  return changes;
}

/** Pending changes per section code, for the tab badges. */
export function pendingBySection(
  catalog: SettingsCatalog,
  draft: Draft,
): Readonly<Record<string, number>> {
  const counts: Record<string, number> = {};
  for (const section of catalog.sections) {
    counts[section.code] = section.settings.filter(
      (setting) => isEditable(setting) && isChanged(setting, draft),
    ).length;
  }
  return counts;
}

/**
 * What a valid value looks like, in the SAME words the server uses.
 *
 * The server's message is the authority; this only lets the control say it
 * before a round trip.
 */
export function expectation(setting: Setting): string {
  switch (setting.value_type) {
    case 'INTEGER': {
      const low = setting.min_value;
      const high = setting.max_value;
      if (low != null && high != null) return `Must be a whole number between ${low} and ${high}.`;
      if (low != null) return `Must be a whole number of at least ${low}.`;
      if (high != null) return `Must be a whole number of at most ${high}.`;
      return 'Must be a whole number.';
    }
    case 'BOOLEAN':
      return 'Must be true or false.';
    case 'ENUM':
      return `Must be one of: ${(setting.allowed_values ?? []).join(', ')}.`;
    case 'STRING':
      return 'Must be non-empty text.';
    default:
      return `Settings of type ${setting.value_type} cannot be changed in this version.`;
  }
}

/**
 * The obvious mistake, caught without a round trip -- or null.
 *
 * Assists, never decides: the server re-validates every value regardless
 * (custom requirement 7).
 */
export function localProblem(setting: Setting, draft: Draft): string | null {
  const raw = drafted(setting, draft);
  if (raw === undefined || !isChanged(setting, draft)) return null;
  const value = submittedValue(setting, raw);

  switch (setting.value_type) {
    case 'INTEGER': {
      if (typeof value !== 'number') return expectation(setting);
      if (setting.min_value != null && value < setting.min_value) return expectation(setting);
      if (setting.max_value != null && value > setting.max_value) return expectation(setting);
      return null;
    }
    case 'STRING':
      return typeof value === 'string' && value.trim() ? null : expectation(setting);
    case 'ENUM':
      return typeof value === 'string' && (setting.allowed_values ?? []).includes(value)
        ? null
        : expectation(setting);
    default:
      return null;
  }
}

/** Local problems for every drafted setting, keyed by setting key. */
export function localProblems(
  catalog: SettingsCatalog,
  draft: Draft,
): Readonly<Record<string, string>> {
  const problems: Record<string, string> = {};
  for (const setting of allSettings(catalog)) {
    const problem = localProblem(setting, draft);
    if (problem) problems[setting.key] = problem;
  }
  return problems;
}

/** Server-side rejections keyed by setting key (the first per key wins). */
export function errorsByKey(errors: readonly ErrorItem[]): Readonly<Record<string, string>> {
  const byKey: Record<string, string> = {};
  for (const error of errors) {
    if (!(error.key in byKey)) byKey[error.key] = error.message;
  }
  return byKey;
}

/**
 * The section holding the first problem, in display order -- so a rejection on
 * a hidden tab is brought into view rather than reading as a save that did
 * nothing.
 */
export function firstSectionWithProblem(
  catalog: SettingsCatalog,
  problemKeys: Iterable<string>,
): string | null {
  const keys = new Set(problemKeys);
  for (const section of catalog.sections) {
    if (section.settings.some((setting) => keys.has(setting.key))) return section.code;
  }
  return null;
}

/**
 * A display label for an ENUM option.
 *
 * Upper-case vocabulary words read as `January` rather than shouting; short
 * codes such as `USD` are left alone. Presentation only -- the value sent is
 * always the code itself.
 */
export function optionLabel(value: string): string {
  if (value.length > 3 && /^[A-Z][A-Z_]*$/.test(value)) {
    const words = value.toLowerCase().split('_');
    return words
      .map((word, index) => (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
      .join(' ');
  }
  return value;
}

/** How a stored value reads in a "was …" note. */
export function storedLabel(setting: Setting): string {
  if (setting.value_type === 'BOOLEAN') return setting.value ? 'on' : 'off';
  if (setting.value_type === 'ENUM') return optionLabel(String(setting.value));
  return String(setting.value);
}
